import {
	CellRendererDataTransformer,
	CustomHeaderCellRenderer,
	TransformedSuperGridColDef,
	getLevelByDepth,
	getLevelDepth,
} from '@aleph/supergrid';
import { REDDIT_SUPERGRID_CONFIG } from 'wb-reddit/app/routes/superGrid/config';
import { RedditBreakdowns, RedditLevel } from 'wb-reddit/app/routes/superGrid/utils';
import {
	CommunityBreakdownSuperGridListDocument,
	CountryBreakdownSuperGridListDocument,
	DateBreakdownSuperGridListDocument,
	InterestBreakdownSuperGridListDocument,
	RegionBreakdownSuperGridListDocument,
	AdAccountOrderByColumn, AdOrderByColumn,
	CampaignOrderByColumn,
	GroupOrderByColumn,
	StatsOrderByColumn,
} from 'wb-reddit/graphql/generated';
import {
	ColDef, RowClassParams, IRowNode,
} from 'ag-grid-community';
import { DefaultCellRenderer } from '@aleph/shared-components';

export const getRedditBreakdownQuery = (selectedBreakdown?: RedditBreakdowns) => {
	if (selectedBreakdown) {
		switch (selectedBreakdown) {
			case RedditBreakdowns.DAILY:
				return DateBreakdownSuperGridListDocument;
			case RedditBreakdowns.COMMUNITY:
				return CommunityBreakdownSuperGridListDocument;
			case RedditBreakdowns.INTEREST:
				return InterestBreakdownSuperGridListDocument;
			case RedditBreakdowns.REGION:
				return RegionBreakdownSuperGridListDocument;
			case RedditBreakdowns.COUNTRY:
				return CountryBreakdownSuperGridListDocument;
		}
	}

	return undefined;
};

export const DEFAULT_REDDIT_COL_DEF: ColDef = {
	headerClass:           'border-b border-b-gray-300 border-r border-r-gray-300',
	headerComponent:       CustomHeaderCellRenderer,
	headerComponentParams: params => {
		const level = params?.column?.getColDef()?.level;

		const color = params?.context?.config?.levels?.find(col => col?.level === level)?.anchors?.color;

		return {
			color: color ?? 'hsl(var(--gray-950))',
		};
	},
	cellRenderer:       CellRendererDataTransformer,
	cellRendererParams: {
		originalRenderer: DefaultCellRenderer,
	},
	sortable:  true,
	resizable: true,
	cellStyle: {
		borderRightWidth: 1,
		borderRightColor: 'hsl(var(--gray-300))',
		padding:          0,
	},
	cellClass: params => {
		if ((params?.context?.validations ?? []).some(
			validation => (validation.field === (params?.colDef?.colId ?? params?.colDef?.field))
				&& (
					(validation.id === params?.data?.id && params?.data?.id)
					|| (validation.id === params.data?.hash && params?.data?.hash)
				),
		)) {
			return 'is-invalid';
		}

		return undefined;
	},
};

const isRowStartOfNextLevel = (level: RedditLevel) => (params: RowClassParams) => {
	let nextNode: IRowNode | undefined;

	// getDisplayedRowNodeAtIndex is not working properly in the ag grid, so we have to do this
	params.api?.forEachNode((node) => {
		if (node.rowIndex === params.rowIndex + 1) {
			nextNode = node;
		}
	});

	const isNextDeeper = (nextNode?.level ?? -1) > params.node?.level;

	return isNextDeeper && params?.data?.level === level;
};

const isRowEndOfLevel = (level: RedditLevel) => (params: RowClassParams) => {
	if (isRowStartOfNextLevel(level)(params)) {
		return false;
	}

	let nextNode: IRowNode | undefined;

	// getDisplayedRowNodeAtIndex is not working properly in the ag grid, so we have to do this
	params.api?.forEachNode((node) => {
		if (node.rowIndex === params.rowIndex + 1) {
			nextNode = node;
		}
	});

	const isLastRowOfLevel = params?.data?.isLastRow;

	if (params?.data?.level === RedditLevel.BREAKDOWN) {
		return isLastRowOfLevel;
	}

	return isLastRowOfLevel && params?.data?.level === level && !!nextNode;
};

export const getRedditSuperGridRowClassRules = () => ({
	'big-account': (params: RowClassParams) => params.node?.data?.level === RedditLevel.AD_ACCOUNT
			&& !params.node.expanded
			&& !params.node.isRowPinned()
			&& getLevelByDepth(
				REDDIT_SUPERGRID_CONFIG,
				params.context.gridLevelDepth,
			) === RedditLevel.AD_ACCOUNT,
	'bg-max-level': (params: RowClassParams) => params.node.level <= params.context.gridLevelDepth
			&& params.node.level > 0,
	'bg-level-collapsed': (params: RowClassParams) => params.node.level < params.context.gridLevelDepth
			&& params.node.level < (getLevelDepth(REDDIT_SUPERGRID_CONFIG, RedditLevel.GROUP))
            && params.data?.level !== RedditLevel.BREAKDOWN,
	'bg-level-expanded': (params: RowClassParams) => params.node.expanded,

	'row-level-ad-account': (params: RowClassParams) => params.data?.level === RedditLevel.AD_ACCOUNT,
	'row-level-campaign':   (params: RowClassParams) => params.data?.level === RedditLevel.CAMPAIGN,
	'row-level-group':      (params: RowClassParams) => params.data?.level === RedditLevel.GROUP,
	'row-level-ad':         (params: RowClassParams) => params.data?.level === RedditLevel.AD,
	'row-top-expand-level': (params: RowClassParams) => (params.rowIndex ?? 0) - (params.node?.parent?.rowIndex ?? 0) === 1
				&& params.node?.level !== 0,
	'row-expanded': (params: RowClassParams) => params.node?.expanded,

	'row-level-campaign-start': isRowStartOfNextLevel(RedditLevel.AD_ACCOUNT),
	'row-level-group-start':    isRowStartOfNextLevel(RedditLevel.CAMPAIGN),
	'row-level-ad-start':       isRowStartOfNextLevel(RedditLevel.GROUP),

	'row-level-campaign-end': isRowEndOfLevel(RedditLevel.CAMPAIGN),
	'row-level-group-end':    isRowEndOfLevel(RedditLevel.GROUP),
	'row-level-ad-end':       isRowEndOfLevel(RedditLevel.AD),

	'row-level-breakdown': (params: RowClassParams) => params.data?.level === RedditLevel.BREAKDOWN
    || params.data?.isBreakdownParent,
});

export const mapSortField = (gridField: string, level: RedditLevel) => {
	switch (true) {
		// Campaign fields
		case gridField === 'campaignFirst':
			return 'campaignId';
		case gridField === 'status':
			if (level === RedditLevel.AD_ACCOUNT) return null;
			if (level === RedditLevel.CAMPAIGN) return 'campaignStatus';
			if (level === RedditLevel.GROUP) return 'groupStatus';
			if (level === RedditLevel.AD) return 'adStatus';

			break;

		// Group fields
		case gridField === 'groupFirst':
			return 'groupId';

		// Ad fields
		case gridField === 'adFirst':
			return 'adId';

		case gridField === 'creativeText':
			return 'textValue';

		case gridField.includes('creativeContentCaption'):
			return 'creativeContentCaption';

		case gridField.includes('creativeContentMediaUrl'):
			return 'creativeContentMediaUrl';
	}

	return gridField;
};

export const filterSortField = (gridField: string, level: RedditLevel) => {
	if (Object.values(StatsOrderByColumn).includes(gridField as StatsOrderByColumn)) {
		return true;
	}

	switch (level) {
		case RedditLevel.AD:
			return Object.values(AdOrderByColumn).includes(gridField as AdOrderByColumn);

		case RedditLevel.GROUP:
			return Object.values(GroupOrderByColumn).includes(gridField as GroupOrderByColumn);

		case RedditLevel.CAMPAIGN:
			return Object.values(CampaignOrderByColumn).includes(gridField as CampaignOrderByColumn);

		case RedditLevel.AD_ACCOUNT:
			return Object.values(AdAccountOrderByColumn).includes(gridField as AdAccountOrderByColumn);
	}

	return false;
};

type IsActionCellParams = {
	colDef?: TransformedSuperGridColDef<RedditLevel>,
	data?: Record<string, any>
};

// A cell is a action cell when pressing enter on it will trigger an action
export function isActionCell({ colDef, data }: IsActionCellParams) {
	if (
		colDef?.elasticParent
        || colDef?.colId === 'expand'
        || colDef?.field === 'creativeAllowComments'
        || colDef?.field === 'index'
        || data?.field === 'creativeAllowComments'
        || colDef?.field === 'clientPinnedSettings'
        || data?.supergridIsCreateRow
        || data?.isCreate
	) {
		return true;
	}

	return false;
}
